import { TrashIcon } from '@heroicons/react/24/outline';
import { AlertTriangleIcon, PlusIcon } from '@langchain/untitled-ui-icons';
import { Divider, IconButton, Modal, ModalDialog, Typography } from '@mui/joy';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { Tooltip } from '@/design-system';
import {
  invalidateWorkspaceStatsIfNeeded,
  useOrganizationId,
  useSessionUpdateMutation,
} from '@/hooks/useSwr';
import { SessionSchema } from '@/types/schema';

import {
  createSession as createSessionAPI,
  updateSession as updateSessionAPI,
} from '../../data/mutations';
import {
  apiSessionsPath,
  appOrganizationPath,
  appProjectsPath,
  appSessionPath,
} from '../../utils/constants';
import { createName } from '../../utils/create-name';
import { DatasetPicker } from '../DatasetPicker';
import { ExpandableErrorAlert } from '../ExpandableErrorAlert';
import { Pane } from '../Pane';
import { TextField } from '../TextField';

dayjs.extend(utc);

export interface SessionCrudModalBaseProps {
  session?: SessionSchema;
  onSuccess?: (id: string, session: SessionSchema) => void;
}

interface SessionCrudModalProps extends SessionCrudModalBaseProps {
  session?: SessionSchema;
  onSuccess?: (id: string, session: SessionSchema) => void;
  isOpen: boolean;
  doClose: () => void;
}

const createSessionName = createName.bind(null, 'pt');

function EndProjectModal(props: { id: string }) {
  const [open, setOpen] = useState(false);
  const deleteMutation = useSessionUpdateMutation(props.id);

  useEffect(() => {
    if (!open) deleteMutation.reset();
  }, [open, deleteMutation]);

  return (
    <>
      <Button variant="outlined" color="danger" onClick={() => setOpen(true)}>
        End Project
      </Button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog
          variant="outlined"
          role="alertdialog"
          aria-labelledby="alert-dialog-modal-title"
          aria-describedby="alert-dialog-modal-description"
        >
          <Typography
            id="alert-dialog-modal-title"
            component="h2"
            startDecorator={<AlertTriangleIcon className="size-4" />}
          >
            Confirmation
          </Typography>

          <Divider />

          <Typography
            id="alert-dialog-modal-description"
            textColor="text.tertiary"
          >
            Are you sure you want mark the project as ended?
          </Typography>

          {deleteMutation.error && (
            <ExpandableErrorAlert error={deleteMutation.error} />
          )}

          <Box
            sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 2 }}
          >
            <Button
              type="button"
              variant="soft"
              color="neutral"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                deleteMutation.trigger({
                  json: { end_time: dayjs.utc().toISOString() },
                });
              }}
              loading={deleteMutation.isMutating}
            >
              End project
            </Button>
          </Box>
        </ModalDialog>
      </Modal>
    </>
  );
}

interface MetadataItem {
  key: string;
  value: unknown;
}

export function SessionCrudPane({
  session,
  onSuccess,
  isOpen,
  doClose,
}: SessionCrudModalProps) {
  const organizationId = useOrganizationId();
  const [sessionName, setSessionName] = useState<string>(
    session?.name ? session?.name : createSessionName
  );
  const [description, setDescription] = useState<string>(
    session?.description ? session.description : ''
  );

  // Convert initial metadata object to array
  const [metadataItems, setMetadataItems] = useState<MetadataItem[]>(() => {
    const initialMetadata = session?.extra?.metadata ?? {};
    return Object.entries(initialMetadata).map(([key, value]) => ({
      key,
      value,
    }));
  });

  const [metadataKeyErrors, setMetadataKeyErrors] = useState<{
    [key: number]: string;
  }>({});

  useEffect(() => {
    if (session?.name) {
      setSessionName(session.name);
    }
    if (session?.description) {
      setDescription(session.description);
    }
    if (session?.extra?.metadata) {
      setMetadataItems(
        Object.entries(session.extra.metadata).map(([key, value]) => ({
          key,
          value,
        }))
      );
    }
  }, [session?.name, session?.description, session?.extra?.metadata]);

  const [defaultDatasetId, setDefaultDatasetId] = useState<string | undefined>(
    session?.default_dataset_id
  );

  const nameDisabled = session == null;
  const nameWarning = !session?.end_time
    ? `This project is currently active. If renaming, ensure your application code is updated to use the new project name. New traces sent to the old project name will go to a separate project.`
    : undefined;

  useEffect(() => {
    if (session?.default_dataset_id) {
      setDefaultDatasetId((prev) => {
        if (prev == null && session?.default_dataset_id) {
          return session.default_dataset_id;
        }
        return prev;
      });
    }
  }, [session?.default_dataset_id]);

  // TODO replace with useSWRMutation
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const requiredError = 'This field is required';
  const validateInput = useDebouncedCallback((value) => {
    if (value) {
      return setError(undefined);
    }
    setError(requiredError);
  });
  const navigate = useNavigate();

  const createSession = async () => {
    if (sessionName && organizationId) {
      // Convert metadata array to object for API
      const metadata = metadataItems.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {} as { [key: string]: unknown });

      const result = await createSessionAPI(organizationId, {
        name: sessionName,
        description: description ?? undefined,
        extra: { metadata },
        default_dataset_id: defaultDatasetId ?? undefined,
      });

      // Invalidate workspace stats after successful session creation
      invalidateWorkspaceStatsIfNeeded(apiSessionsPath, 'POST');

      return result;
    }
  };

  const updateSession = async () => {
    if (sessionName && organizationId && session) {
      // Convert metadata array to object for API
      const metadata = metadataItems.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {} as { [key: string]: unknown });

      const result = await updateSessionAPI(organizationId, session.id, {
        name: sessionName,
        description: description ?? undefined,
        extra: {
          ...session.extra,
          metadata,
        },
        default_dataset_id:
          defaultDatasetId ?? '00000000-0000-0000-0000-000000000000',
      });

      // Invalidate workspace stats after successful session update
      invalidateWorkspaceStatsIfNeeded(
        `${apiSessionsPath}/${session.id}`,
        'PATCH'
      );

      return result;
    }
  };

  const createOrUpdateSession = async () => {
    const promise = session ? updateSession() : createSession();
    type dataType = SessionSchema | JSX.Element | undefined;
    await promise
      .then((data: dataType) => {
        const isSessionSchema = (
          variable: dataType
        ): variable is SessionSchema => {
          return (
            variable !== undefined &&
            variable &&
            typeof variable === 'object' &&
            'id' in variable
          );
        };
        if (isSessionSchema(data)) {
          if (onSuccess) {
            onSuccess(data.id, data);
          }
          const { id } = data;
          if (session) {
            doClose();
          } else {
            navigate(
              `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${id}`
            );
          }
        }
      })
      .catch((error) => {
        setError(error.message);
      });
  };

  const updateSessionName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const userInput = e.target.value;
    if (!nameDisabled) {
      validateInput(userInput);
      setSessionName(userInput);
    }
  };

  const updateDescription = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  };

  const handleDeleteMetadata = (index: number) => {
    setMetadataItems((current) => current.filter((_, i) => i !== index));
    setMetadataKeyErrors({});
  };

  const handleAddMetadata = () => {
    const newKey = `key${metadataItems.length}`;
    setMetadataItems((current) => [...current, { key: newKey, value: '' }]);
  };

  const handleChangeMetadataKey = (index: number, newKey: string) => {
    setMetadataItems((current) =>
      current.map((item, i) => (i === index ? { ...item, key: newKey } : item))
    );

    // Check for duplicates
    const duplicateKeys = metadataItems
      .map((item, i) => i !== index && item.key === newKey)
      .filter(Boolean);

    if (duplicateKeys.length > 0) {
      setMetadataKeyErrors((prev) => ({
        ...prev,
        [index]: 'This key already exists',
      }));
    } else {
      setMetadataKeyErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const handleChangeMetadataValue = (index: number, value: string) => {
    const formattedValue =
      !value.trim() || isNaN(Number(value)) ? value : parseFloat(value);
    setMetadataItems((current) =>
      current.map((item, i) =>
        i === index ? { ...item, value: formattedValue } : item
      )
    );
  };

  const paneTitle = session ? 'Edit Project' : 'New Project';

  async function handleSubmit() {
    // Check for duplicate keys before submitting
    const keys = metadataItems.map((item) => item.key);
    const hasDuplicates = keys.length !== new Set(keys).size;

    if (hasDuplicates) {
      // Update error state for all duplicate keys
      const keyCount = keys.reduce((acc, key) => {
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {} as { [key: string]: number });

      const newErrors = {};
      metadataItems.forEach((item, index) => {
        if (keyCount[item.key] > 1) {
          newErrors[index] = 'Duplicate keys are not allowed';
        }
      });
      setMetadataKeyErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await createOrUpdateSession();
    } finally {
      setIsSubmitting(false);
    }
  }

  const cancelAndSubmitButtons = (
    <div className="mr-4 flex items-center gap-2">
      <div>
        <Button
          type={'button'}
          onClick={doClose}
          color="neutral"
          variant="outlined"
        >
          Cancel
        </Button>
      </div>
      <div>
        <Button
          type="submit"
          color="primary"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </div>
    </div>
  );

  return (
    <Pane
      open={isOpen}
      onClose={doClose}
      title={paneTitle}
      topBarRightElement={cancelAndSubmitButtons}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
      >
        <div className="grid grid-cols-[minmax(120px,1fr),minmax(auto,800px),1fr] gap-x-8 gap-y-5">
          <div className="flex flex-col items-start">
            <strong className="font-semibold">Project Details</strong>
            <span className="text-sm text-tertiary">
              Basic details about your tracing project
            </span>
          </div>

          <div className="flex flex-col gap-4">
            {!nameDisabled ? (
              <TextField
                label="Name"
                helperText={error || nameWarning}
                onChange={updateSessionName}
                placeholder="Type name..."
                value={sessionName}
                error={!!error}
                required
                helperTextStyle={nameWarning ? { color: '#D52E37' } : {}}
              />
            ) : (
              <Tooltip
                title="Project is still active. Name cannot be changed."
                side="top"
                align="start"
              >
                <div>
                  <TextField
                    label="Name"
                    placeholder="Type name..."
                    value={sessionName}
                    disabled
                  />
                </div>
              </Tooltip>
            )}
            <TextField
              label="Description"
              onChange={updateDescription}
              placeholder="Add a description..."
              value={description}
            />

            <DatasetPicker
              label="Default Dataset"
              datasetId={defaultDatasetId}
              onChange={setDefaultDatasetId}
            />
          </div>

          <div />

          <hr className="col-start-1 col-end-4 border-tertiary" />

          <div className="flex flex-col items-start">
            <strong className="font-semibold">Metadata</strong>
            <p className="text-sm text-tertiary">
              Structured data about your project
            </p>
          </div>

          <div className="flex flex-col items-start gap-4">
            {metadataItems.map((item, index) => (
              <div className="flex items-start gap-2" key={index}>
                <TextField
                  label=""
                  value={item.key}
                  onChange={(e) =>
                    handleChangeMetadataKey(index, e.target.value)
                  }
                  placeholder="Key"
                  variant="outlined"
                  error={!!metadataKeyErrors[index]}
                  helperText={metadataKeyErrors[index]}
                />
                <TextField
                  label=""
                  value={
                    typeof item.value === 'string' ||
                    typeof item.value === 'number' ||
                    Array.isArray(item.value) ||
                    item.value === undefined
                      ? item.value
                      : JSON.stringify(item.value)
                  }
                  onChange={(e) =>
                    handleChangeMetadataValue(index, e.target.value)
                  }
                  placeholder="Value"
                  variant="outlined"
                />
                <IconButton
                  color="neutral"
                  onClick={() => handleDeleteMetadata(index)}
                  variant="outlined"
                  aria-label="Delete"
                >
                  <TrashIcon className="h-5 w-5" />
                </IconButton>
              </div>
            ))}
            <Button
              onClick={handleAddMetadata}
              variant="outlined"
              color="neutral"
              startDecorator={<PlusIcon className="size-4" />}
            >
              Add Metadata
            </Button>
          </div>

          <div />

          <hr className="col-start-1 col-end-4 border-tertiary" />

          <div />

          {session != null &&
          session.end_time == null &&
          session.reference_dataset_id != null ? (
            <>
              <hr className="col-start-1 col-end-4 border-tertiary" />

              <Fragment>
                <div className="flex flex-col items-start">
                  <strong className="font-semibold">Actions</strong>
                </div>

                <div>
                  <div className="flex items-center gap-2 rounded-lg border border-error p-4">
                    <div className="flex-grow">
                      <strong className="text-sm font-semibold">
                        End project
                      </strong>
                      <p className="text-sm">
                        Ends the project and stops collecting data.
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <EndProjectModal id={session.id} />
                    </div>
                  </div>
                </div>
              </Fragment>
            </>
          ) : null}
        </div>
      </form>
    </Pane>
  );
}
